import {
  Component,
  OnInit,
  ViewEncapsulation,
  Inject,
  PLATFORM_ID,
  AfterViewInit,
  ChangeDetectorRef,
  ElementRef,
  ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';
import { LocaleService } from '@app/core/i18n';
import { environment } from '@env/environment';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { UserUtilsService } from '@app/library/services/user-utils.service';
import { IPageSimple } from '@vestiaire/vc-angular-lib';
import { PageService } from '@app/core/content/services';
import { SetPageParams } from '@app/core/header/store/actions';
import { Persist } from '@app/analytics/actions/data-layer';
import { HomeMicrodatasService } from '@app/home/services/home-microdatas.service';
import { BasePageComponentDirective } from '@app/library/components/base-page.component';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';
import { SearchStateMappingService } from '@app/core/search/services';
import { TransferState, Meta } from '@angular/platform-browser';
import { defaultMetas } from '@app/shared/static/metas';
import { take, takeUntil } from 'rxjs/operators';
import * as fromI18n from '@app/core/i18n/store/reducers';
import { UserService } from '@app/core/user';
import * as fromUser from '@app/core/user/store/reducers';
import { Subject } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { UsersService } from '@vestiaire/api-sdk-angular';

@Component({
  selector: 'vc-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class HomeComponent extends BasePageComponentDirective implements OnInit, AfterViewInit {
  @ViewChild('ctaTranslation', { read: ElementRef }) ctaTranslation: ElementRef;

  public schemas = { search: {}, organization: {} };
  public ctaListPost: { title: string; path: string; target: string };
  // Came from the resolver
  public homePageFromResolver: IPageSimple;

  // Mysizes on HP
  private ngUnsubscribe = new Subject<void>();
  public isServer = false;
  public showMysizesLabel = false;

  constructor(
    private userServices: UsersService,
    private _pageService: PageService,
    private _homeMicrodatasService: HomeMicrodatasService,
    private _ref: ChangeDetectorRef,
    protected meta: Meta,
    protected readonly store$: Store<IAppState>,
    protected readonly localeService: LocaleService,
    protected readonly transferState: TransferState,
    protected readonly helpersService: HelpersService,
    protected readonly platformService: PlatformService,
    protected readonly userUtilsService: UserUtilsService,
    protected searchStateMappingService: SearchStateMappingService,
    @Inject(PLATFORM_ID) protected platformId: Object,
    public route: ActivatedRoute,
    protected readonly userService: UserService
  ) {
    super(
      store$,
      localeService,
      platformService,
      userUtilsService,
      helpersService,
      searchStateMappingService,
      transferState,
      platformId,
      'home',
      userService
    );
    this.isServer = isPlatformServer(this.platformId);
    this.route.data.subscribe(
      data => {
        if (data.CMS) {
          this.homePageFromResolver = data.CMS;

          this.store$.dispatch(
            new Persist({
              ...data.pageAnalytics,
              pageUrl: data.CMS.tracking
            })
          );
        }
      },
      error => {
        console.log('CMS Home page error Router subscribe :', error);
      }
    );

    // Dispach the [Navigation] set page parameters
    this.store$.dispatch(new SetPageParams({ pageType: 'homepage' }));

    // Add meta tag for Korean SEO on www domain
    this.store$.pipe(select(fromI18n.getUserLang), take(1)).subscribe(
      language => {
        if (language.isoCode === 'en-GB') {
          this.meta.addTag({ name: 'naver-site-verification', content: 'be49eaf8a119e88ca4b13cc01631740a25e0f938' });
        }
      },
      () => {
        /* Silently catch exception */
      }
    );
  }

  ngOnInit(): void {
    if (environment.feature.journal_main) {
      this.ctaListPost = {
        title: '',
        path: '/journal/',
        target: '_self'
      };
    }
    this._initMetadata();

    this._pageService.initAlternateLinks('/');
    this.schemas = this._homeMicrodatasService.getDatas();
  }

  ngAfterViewInit() {
    if (environment.feature.journal_main) {
      this.ctaListPost.title = this.ctaTranslation.nativeElement.getAttribute('title');
      this._ref.detectChanges();
    }

    // Mysizes on HP
    if (!this.isServer) {
      this.store$.pipe(select(fromUser.isAuthenticated), takeUntil(this.ngUnsubscribe)).subscribe(
        res => {
          if (!res) {
            this.showMysizesLabel = false;
          } else {
            this.userServices.getSearchPreferences().subscribe({
              next: ({ data }) => {
                if (data?.universe?.ids?.length > 0) {
                  // user has mysizes set already
                  this.showMysizesLabel =
                    this.pageType === 'home' &&
                    environment?.feature?.enable_my_sizes_on_hp &&
                    this.isBlockPersonalisable;
                }
              },
              error: () => {}
            });
          }
        },
        err => console.log(err)
      );
    }
  }

  private _initMetadata(): void {
    const defaultMetaLanguage = defaultMetas[this.localeService.getLanguage()];
    this._pageService.setRobots({ index: true, follow: true });
    this._pageService.setDescription(defaultMetaLanguage.description);
    this._pageService.setKeywords(defaultMetaLanguage.keywords);
    this._pageService.setTitle(defaultMetaLanguage.title);
  }

  updateShowMysizesLabel(val: boolean) {
    this.showMysizesLabel = val;
  }
}
